// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
import type { EmailConfidence } from "@crm-src/types/lead"

/**
 * A single emailable AI-prospect contact, flattened out of an AI-generated CRM
 * lead. AI prospects keep their buyer emails on `customer_contacts` rows (the
 * lead's own primary email is blank), which is why they fall out of the plain
 * Prospects tab — this surfaces those contact emails to the recipient picker.
 */
export interface AiProspectRecipient {
  id: string
  name: string
  email: string
  company?: string
  /** null when the lead predates email-confidence capture, or the address was
   * a prose-scraped generic inbox — rendered as "unverified". */
  email_confidence: EmailConfidence | null
}

/** The minimal slice of a CRM lead this flatten needs — a structural subset of
 * `Lead` so it doesn't couple to the full `CustomerContact`/`Lead` shape (and
 * stays trivially unit-testable). A real `Lead` satisfies it. */
export interface AiProspectLeadLike {
  company_name?: string | null
  full_name?: string | null
  customer_contacts?: Array<{
    id: string | number
    first_name?: string | null
    last_name?: string | null
    full_name?: string | null
    email?: string | null
    email_confidence?: EmailConfidence | null
  }>
}

/** Verified/likely addresses are safe to default-select; guessed/unknown/null
 * are shown but flagged so a campaign never blasts a risky address by accident. */
export function isDeliverableConfidence(c: EmailConfidence | null | undefined): boolean {
  return c === "verified" || c === "likely"
}

function contactName(c: { first_name?: string | null; last_name?: string | null; full_name?: string | null }, fallback: string): string {
  const full = (c.full_name || "").trim()
  if (full) return full
  const joined = [c.first_name, c.last_name].filter(Boolean).join(" ").trim()
  return joined || fallback
}

/**
 * Flatten AI-generated leads into deduped emailable contact rows. Dedupes by
 * lower-cased email across ALL leads (the same buyer email can appear on more
 * than one lead). Contacts with no email are dropped (can't be emailed).
 */
export function aiProspectRecipientsFromLeads(leads: AiProspectLeadLike[]): AiProspectRecipient[] {
  const out: AiProspectRecipient[] = []
  const seen = new Set<string>()

  for (const lead of leads) {
    const company = (lead.company_name || lead.full_name || "").trim()
    for (const c of lead.customer_contacts ?? []) {
      const email = (c.email || "").trim()
      if (!email) continue
      const key = email.toLowerCase()
      if (seen.has(key)) continue
      seen.add(key)
      out.push({
        id: `ai_${c.id}`,
        name: contactName(c, email),
        email,
        ...(company ? { company } : {}),
        email_confidence: c.email_confidence ?? null,
      })
    }
  }

  return out
}
